import { z } from 'zod';

/**
 * Общие примитивы контрактов. Вынесены отдельно, чтобы модули могли импортировать
 * Id / PageQuery без циклической зависимости через index.ts.
 */

/** Идентификаторы: UUIDv7 из базы, поэтому сортировка по id = сортировка по времени. */
export const Id = z.uuid();
export const TenantId = Id;

/**
 * Конверт ошибки. Один формат на все сервисы: клиенту не приходится угадывать,
 * что означает ответ, а интерфейс разрешения отказов строится на `code`.
 */
export const ApiError = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  /** Ошибки по полям формы: путь → что не так. */
  fields: z.record(z.string(), z.string()).optional(),
  /** Идентификатор запроса для поиска в логах. */
  requestId: z.string().min(1),
});
export type ApiError = z.infer<typeof ApiError>;

/**
 * Пагинация только по курсору. Смещение (`offset`) даёт пропуски и дубли на
 * данных, которые меняются во время просмотра, а расписание меняется постоянно.
 */
export const PageQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export function pageOf<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
}

/**
 * Тело запроса на изменение: сами изменения и прежние значения изменяемых
 * полей, как их видел клиент.
 *
 * Форма общая для всех сущностей, потому что общее и правило: сервер сравнивает
 * `expected` со своим состоянием и отличает правку вслепую от перезаписи чужого
 * изменения. Ровно то же самое приходит с операцией из очереди устройства —
 * путь записи один, и различаться этим двум телам нечем.
 */
export function updateBodyOf<T extends z.ZodType>(changes: T) {
  return z.object({
    changes,
    expected: z.record(z.string(), z.unknown()).nullable().default(null),
  });
}

/** Заголовки, обязательные для любой операции записи. */
export const HEADERS = {
  /** Ключ идемпотентности: без него запись не выполняется. */
  idempotency: 'idempotency-key',
  /** Версия строки, которую видел клиент: даёт RECORD_CHANGED вместо потери правки. */
  ifMatchVersion: 'if-match-version',
  requestId: 'x-request-id',
  deviceId: 'x-device-id',
  /**
   * Когда сотрудник выполнил действие. Отличается от момента доставки, если
   * запись сделана без сети: приём, проведённый в оффлайне, не должен выглядеть
   * задокументированным задним числом.
   */
  clientTime: 'x-client-time',
} as const;

/**
 * Состояние доступа арендатора: следствие подписки, проекция из billing.
 * Переходы и права в каждом состоянии — в docs/03-modules/13-billing-subscriptions.md.
 */
export const AccessState = z.enum(['trial', 'active', 'grace', 'restricted', 'suspended']);
export type AccessState = z.infer<typeof AccessState>;

export const ClinicRole = z.enum([
  'clinic_owner',
  'clinic_admin',
  'receptionist',
  'doctor',
  'assistant',
  'head_nurse',
  'clinic_accountant',
]);
export type ClinicRole = z.infer<typeof ClinicRole>;

export const LabRole = z.enum(['lab_owner', 'lab_manager', 'technician']);
export type LabRole = z.infer<typeof LabRole>;
export const PlatformRole = z.enum(['platform_support', 'platform_admin']);
export type PlatformRole = z.infer<typeof PlatformRole>;
export const Role = z.union([ClinicRole, LabRole, PlatformRole]);
export type Role = z.infer<typeof Role>;
